import { useI18n } from "hooks/i18n/i18n";
import { useReducer } from "react";

import { convertToWithState } from "../../components/PrefecturesSelector/functions/convertToWithState";
import { AVAILABILITY_STATUS } from "./constants/availabilityStatus";
import { EVENT_TYPE_DATA } from "./constants/eventTypeData";
import { GROUPED_PREFECTURES_BASE } from "./constants/groupedPrefecturesBase";
import { formStateReducer } from "./functioins/formStateReducer";
import { getConvertNameForLocale } from "./functioins/getConvertNameForLocale";

import type { convertQueryToSearchCondition } from "../../../../functions/convertQueryToSearchCondition";
import type { State } from "./types/states";
import type { GetEventSearchConditionsResponse } from "@spwn-portal/grpc";
import type { Dispatch, ReducerAction } from "react";

type Input = {
  genreConditions: GetEventSearchConditionsResponse["genre"] | null;
} & ReturnType<typeof convertQueryToSearchCondition>;

type Output = State & {
  dispatchFormAction: Dispatch<ReducerAction<typeof formStateReducer>>;
};
type UseFormState = (input: Input) => Output;

export const useFormState: UseFormState = (input) => {
  const {
    availabilityStatus,
    endDateFromQuery,
    eventTypeFromQuery,
    genreConditions,
    genreFromQuery,
    prefectureCodesFromQuery,
    startDateFromQuery,
  } = input;
  const { t } = useI18n();
  const convertNameForLocale = getConvertNameForLocale(t);
  const localePrefectures = GROUPED_PREFECTURES_BASE.map(convertNameForLocale);
  const isGenreConditionsLoaded = genreConditions !== null;
  const availabilityStatusCheckboxData = AVAILABILITY_STATUS.map(
    ({ id, label }) =>
      ({
        checkboxId: `${id}`,
        checkboxLabel: t(
          `search.searchModal.searchConditions.availabilityStatus.${label}`
        ),
        isChecked: availabilityStatus?.includes(id) ?? false,
      }) as const
  );
  const genreCheckboxData = isGenreConditionsLoaded
    ? genreConditions.map((condition) => ({
        checkboxId: `${condition}`,
        checkboxLabel: condition,
        isChecked: genreFromQuery?.includes(condition) ?? false,
      }))
    : [];
  const eventTypeCheckboxData = EVENT_TYPE_DATA.map(
    ({ id, label }) =>
      ({
        checkboxId: `${id}`,
        checkboxLabel: t(
          `search.searchModal.searchConditions.eventType.${label}`
        ),
        isChecked: eventTypeFromQuery?.includes(id) ?? false,
      }) as const
  );
  const [formState, dispatchFormAction] = useReducer(formStateReducer, {
    availabilityStatus: {
      categoryLabel: t("search.searchModal.searchIndexes.availabilityStatus"),
      checkboxData: availabilityStatusCheckboxData,
    },
    endDate: endDateFromQuery,
    eventType: {
      categoryLabel: t("search.searchModal.searchIndexes.eventType"),
      checkboxData: eventTypeCheckboxData,
    },
    genre: {
      categoryLabel: t("search.searchModal.searchIndexes.genre"),
      checkboxData: genreCheckboxData,
    },
    prefectures: convertToWithState(
      localePrefectures,
      prefectureCodesFromQuery
    ),
    startDate: startDateFromQuery,
  });

  return {
    ...formState,
    dispatchFormAction,
  };
};
